import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceDot } from "recharts";
import { useAuth } from "../auth/AuthProvider";
import Workout from "../models/Workout";
import WorkoutSession from "../models/WorkoutSession";

const WorkoutDetailPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { workoutId } = useParams();

  const [workout, setWorkout] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && workoutId) {
      loadData();
    }
  }, [user, workoutId]);

  const loadData = async () => {
    try {
      const [workoutData, sessionList, statsData] = await Promise.all([
        Workout.getById(user.uid, workoutId),
        WorkoutSession.getByWorkoutId(user.uid, workoutId),
        WorkoutSession.getStats(user.uid, workoutId),
      ]);

      setWorkout(workoutData);
      setSessions(sessionList);
      setStats(statsData);
    } catch (err) {
      console.error("Error loading workout:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + "T12:00:00");
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const formatFullDate = (dateStr) => {
    const date = new Date(dateStr + "T12:00:00");
    return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  };

  const getTrendIcon = (trend) => {
    if (trend === null) return null;
    if (trend > 5) return { icon: "\u2191", color: "text-green-500", label: `+${trend}%` };
    if (trend < -5) return { icon: "\u2193", color: "text-red-500", label: `${trend}%` };
    return { icon: "\u2192", color: "text-gray-500", label: "stable" };
  };

  // Prepare chart data (oldest to newest for proper display)
  const chartData = sessions
    .slice()
    .reverse()
    .map((session) => ({
      date: formatDate(session.date),
      fullDate: session.date,
      result: session.result,
      isPB: session.result === stats?.personalBest,
    }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">{formatFullDate(data.fullDate)}</p>
          <p className="text-lg font-bold text-gray-900 dark:text-white">
            {data.result} {workout?.measurementUnit}
            {data.isPB && <span className="ml-2 text-yellow-500">PR!</span>}
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          Loading...
        </div>
      </div>
    );
  }

  if (!workout) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          Workout not found
        </div>
      </div>
    );
  }

  const trend = getTrendIcon(stats?.trend);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <button
        onClick={() => navigate("/workouts")}
        className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6"
      >
        <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>

      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden mb-6">
        {workout.img && (
          <div className="h-40 sm:h-48 overflow-hidden">
            <img
              src={workout.img}
              alt={workout.name}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div className="p-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {workout.name}
          </h1>
          {workout.description && (
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {workout.description}
            </p>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats?.totalSessions || 0}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Sessions</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-yellow-500">
                {stats?.personalBest !== null ? stats.personalBest : "-"}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Best ({workout.measurementUnit})</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats?.average !== null ? stats.average : "-"}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Average</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-center">
              {trend ? (
                <>
                  <div className={`text-2xl font-bold ${trend.color}`}>
                    {trend.icon} {trend.label}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Trend</div>
                </>
              ) : (
                <>
                  <div className="text-2xl font-bold text-gray-400">-</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Trend</div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Performance Chart */}
      {sessions.length >= 2 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Performance Over Time
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <XAxis
                  dataKey="date"
                  tick={{ fill: "#9CA3AF", fontSize: 12 }}
                  axisLine={{ stroke: "#374151" }}
                  tickLine={{ stroke: "#374151" }}
                />
                <YAxis
                  tick={{ fill: "#9CA3AF", fontSize: 12 }}
                  axisLine={{ stroke: "#374151" }}
                  tickLine={{ stroke: "#374151" }}
                  domain={["dataMin - 1", "dataMax + 1"]}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="result"
                  stroke="#6366f1"
                  strokeWidth={2}
                  dot={{ fill: "#6366f1", r: 4 }}
                  activeDot={{ r: 6, fill: "#6366f1" }}
                />
                {/* Highlight personal best */}
                {chartData.filter((d) => d.isPB).map((d, i) => (
                  <ReferenceDot
                    key={i}
                    x={d.date}
                    y={d.result}
                    r={8}
                    fill="#EAB308"
                    stroke="#EAB308"
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Session History */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Session History
        </h2>

        {sessions.length === 0 ? (
          <p className="text-center text-gray-500 dark:text-gray-400 py-8">
            No sessions logged yet. Start by logging your first session!
          </p>
        ) : (
          <div className="space-y-3">
            {sessions.map((session) => (
              <div
                key={session.id}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
              >
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {formatFullDate(session.date)}
                  </div>
                  {session.notes && (
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {session.notes}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold text-gray-900 dark:text-white">
                    {session.result} {workout.measurementUnit}
                    {session.result === stats?.personalBest && (
                      <span className="ml-2 text-yellow-500 text-sm">PR!</span>
                    )}
                  </div>
                  {session.perceivedEffort && (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      RPE: {session.perceivedEffort}/10
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Log Session Button */}
      <div className="text-center">
        <button
          onClick={() => navigate(`/log-workout?workout=${workoutId}`)}
          className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
        >
          Log Session
        </button>
      </div>
    </div>
  );
};

export default WorkoutDetailPage;
